import { useController, type FieldError } from 'react-hook-form';

export type LeaseField<TValue> = {
  value: TValue;
  /** Writes the value, marks the field dirty and validates per the form's `mode`. */
  onChange: (value: TValue) => void;
  /** Marks the field touched and validates per the form's `mode`. */
  onBlur: () => void;
  /** Attach to the focusable element so a failed submit can focus the first invalid field. */
  ref: (instance: unknown) => void;
  error: FieldError | undefined;
  isDirty: boolean;
};

/**
 * The engine's only binding to RHF for a single field. Built on `useController`, so the caller
 * re-renders when this field's value or state changes — never on changes elsewhere in the form.
 *
 * Do not read `useFormContext().formState` or call `watch()` during render inside the engine: both
 * subscribe the root `useForm` host and re-render the whole form on every keystroke.
 */
export const useLeaseField = <TValue = unknown>(name: string): LeaseField<TValue> => {
  const { field, fieldState } = useController({ name });
  return {
    value: field.value as TValue,
    onChange: field.onChange as (value: TValue) => void,
    onBlur: field.onBlur,
    ref: field.ref as (instance: unknown) => void,
    error: fieldState.error,
    isDirty: fieldState.isDirty,
  };
};
